import { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Steps } from 'primereact/steps';
import useNotify from '../../../../hooks/useNotify';
import { BillingDocument, RegisterOrderResponse } from '../../types/billing.types';
import DocumentsStep from './DocumentsStep';
import InvoiceStep, { InvoiceStepResult } from './invoice/InvoiceStep';
import OrderStep from './order/OrderStep';
import PaymentStep, { PaymentStepResult } from './payment/PaymentStep';

/** Por dónde arranca el cobro: registrar la orden del turno, facturar una orden pendiente o cobrar facturas con saldo. */
export type BillingStart =
  | { kind: 'order'; appointmentId: number; patientId: number; patientName: string; subtitle?: string }
  | { kind: 'invoice'; orderId: number; patientId: number; patientName: string; subtitle?: string }
  | { kind: 'payment'; patientId: number; patientName: string; subtitle?: string };

type Step =
  | { kind: 'order' }
  | { kind: 'invoice'; orderId: number }
  | { kind: 'payment'; invoiceIds: number[] }
  | { kind: 'documents'; documents: BillingDocument[] };

interface BillingScreenProps {
  start: BillingStart;
  /** Se llama una sola vez, al terminar o salir; dice si se llegó a registrar una orden (o se asociaron órdenes). */
  onFinished: (outcome: { orderRegistered: boolean }) => void;
}

const STEPS = [{ label: 'Orden' }, { label: 'Factura' }, { label: 'Recibo' }, { label: 'Comprobantes' }];
const STEP_INDEX: Record<Step['kind'], number> = { order: 0, invoice: 1, payment: 2, documents: 3 };

/**
 * "Cobro del turno": la orden, la factura al paciente, el recibo y los comprobantes en una pantalla con pasos, con los
 * mismos campos y reglas que las ventanas de la agenda de Vaadin. La orden del paciente queda pendiente de facturar si
 * se omite la factura, y la factura queda con saldo si no se cobra.
 */
export default function BillingScreen({ start, onFinished }: BillingScreenProps) {
  const notify = useNotify();
  const [step, setStep] = useState<Step>(() => {
    if (start.kind === 'order') return { kind: 'order' };
    if (start.kind === 'invoice') return { kind: 'invoice', orderId: start.orderId };
    return { kind: 'payment', invoiceIds: [] };
  });
  const [budget, setBudget] = useState(false);
  const orderRegistered = useRef(false);
  const exitRef = useRef<(() => void) | null>(null);

  const finish = () => onFinished({ orderRegistered: orderRegistered.current });

  const back = () => {
    if (budget) {
      setBudget(false);
      return;
    }
    if (exitRef.current) exitRef.current();
    else finish();
  };

  const handleRegistered = (result: RegisterOrderResponse) => {
    orderRegistered.current = true;
    if (result.hasPatientOrder && result.patientOrderId) {
      exitRef.current = null;
      setStep({ kind: 'invoice', orderId: result.patientOrderId });
      return;
    }
    notify.success('Orden registrada');
    finish();
  };

  const handleInvoiceClosed = (result: InvoiceStepResult) => {
    exitRef.current = null;
    if (result.kind === 'skipped') {
      notify.success('La orden quedó pendiente de facturar');
      finish();
    } else if (!result.payable) {
      notify.success('Factura registrada: cobrala desde el turno cuando AFIP la autorice');
      finish();
    } else {
      setStep({ kind: 'payment', invoiceIds: [result.invoiceId] });
    }
  };

  const handlePaymentClosed = (result: PaymentStepResult | null) => {
    exitRef.current = null;
    if (!result) {
      finish();
      return;
    }
    setStep({
      kind: 'documents',
      documents: [
        ...result.invoices.map((invoice): BillingDocument => ({
          adminFormId: invoice.invoiceId,
          label: `${invoice.formType} ${invoice.formNumber}`.trim(),
          kind: 'invoice',
        })),
        { adminFormId: result.receiptId, label: 'Recibo de pago', kind: 'receipt' },
      ],
    });
  };

  return (
    <div className="nv-scope nv-page">
      <header className="nv-page-head">
        <Button type="button" link icon="pi pi-angle-double-left" label="Volver" className="nv-back" onClick={back} />
        <h1 className="nv-h1">{budget ? 'Presupuesto' : 'Cobro del turno'}</h1>
        <span className="nv-subtitle">{[start.patientName, start.subtitle].filter(Boolean).join(' · ')}</span>
      </header>

      {!budget && (
        // TODO(design-system): Novarium no define el estilo de Steps; se usan los tokens de las pestañas
        <Steps model={STEPS} activeIndex={STEP_INDEX[step.kind]} readOnly className="nv-steps" />
      )}

      {step.kind === 'order' && start.kind === 'order' && (
        <>
          <div className="nv-step-body" hidden={budget}>
            <OrderStep
              appointmentId={start.appointmentId}
              patientName={start.patientName}
              mode="order"
              exitRef={exitRef}
              onRegistered={handleRegistered}
              onCancel={finish}
              onBudget={() => setBudget(true)}
              onAssociated={() => {
                orderRegistered.current = true;
                finish();
              }}
            />
          </div>
          {budget && (
            <div className="nv-step-body">
              <OrderStep appointmentId={start.appointmentId} patientName={start.patientName} mode="budget" onCancel={() => setBudget(false)} />
            </div>
          )}
        </>
      )}

      {step.kind === 'invoice' && <InvoiceStep orderId={step.orderId} exitRef={exitRef} onClose={handleInvoiceClosed} />}

      {step.kind === 'payment' && (
        <PaymentStep patientId={start.patientId} preselectedInvoiceIds={step.invoiceIds} exitRef={exitRef} onClose={handlePaymentClosed} />
      )}

      {step.kind === 'documents' && (
        <DocumentsStep patientId={start.patientId} documents={step.documents} exitRef={exitRef} onClose={finish} />
      )}
    </div>
  );
}
